import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  TableHead,
  Stack
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Group raw materials by item description and collect dates
const groupRawMaterialsByItem = (rawMaterialUsage) => {
  const grouped = {};
  
  rawMaterialUsage.forEach((usage) => {
    const key = usage.itemDescription || 'Unknown Item';
    if (!grouped[key]) {
      grouped[key] = {
        itemDescription: key,
        vendor: usage.vendor || '-',
        lotNumber: usage.lotNumber || '-',
        dates: [],
        totalQuantity: 0,
        costPerItem: parseFloat(usage.costPerItem) || 0,
        totalCost: 0
      };
    }
    
    // Add date if not already present
    const dateStr = formatDate(usage.dateUsed);
    if (!grouped[key].dates.includes(dateStr)) {
      grouped[key].dates.push(dateStr);
    }
    
    grouped[key].totalQuantity += usage.quantityUsed || 0;
    grouped[key].totalCost += (parseFloat(usage.costPerItem) || 0) * (usage.quantityUsed || 0);
  });
  
  // Sort dates for each item
  Object.values(grouped).forEach(item => {
    item.dates.sort((a, b) => new Date(a) - new Date(b));
  });
  
  return Object.values(grouped);
};

// Generate PDF report
const generatePDFReport = (donor, groupedMaterials, totalCost, totalQuantity) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPosition = 30;

  // Header with donor ID
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('Raw Materials Report', margin, yPosition);
  
  yPosition += 15;
  doc.setFontSize(16);
  doc.text(`Donor ID: ${donor?.donorId || 'N/A'}`, margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, margin, yPosition);

  yPosition += 20;

  // Table header
  doc.setFont(undefined, 'bold');
  doc.text('Item Description', margin, yPosition);
  doc.text('Dates Used', margin + 80, yPosition);
  doc.text('Qty', margin + 130, yPosition);
  doc.text('Unit Cost', margin + 150, yPosition);
  doc.text('Total', margin + 170, yPosition);
  
  yPosition += 5;
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Table rows
  doc.setFont(undefined, 'normal');
  groupedMaterials.forEach((item) => {
    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 30;
    }

    // Item description (truncate if too long)
    const itemDesc = item.itemDescription.length > 25 
      ? item.itemDescription.substring(0, 25) + '...' 
      : item.itemDescription;
    doc.text(itemDesc, margin, yPosition);
    
    // Dates (show first few, then "...")
    const datesText = item.dates.length > 3 
      ? item.dates.slice(0, 3).join(', ') + '...'
      : item.dates.join(', ');
    doc.text(datesText, margin + 80, yPosition);
    
    // Quantity, unit cost, total
    doc.text(item.totalQuantity.toString(), margin + 130, yPosition);
    doc.text(`$${item.costPerItem.toFixed(2)}`, margin + 150, yPosition);
    doc.text(`$${item.totalCost.toFixed(2)}`, margin + 170, yPosition);
    
    yPosition += 8;
    
    // Add lot and vendor info on next line
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Lot: ${item.lotNumber} | Vendor: ${item.vendor}`, margin + 5, yPosition);
    doc.setFontSize(12);
    doc.setTextColor(0);
    
    yPosition += 10;
  });

  // Total line
  yPosition += 5;
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;
  
  doc.setFont(undefined, 'bold');
  doc.text('TOTAL:', margin, yPosition);
  doc.text(totalQuantity.toString(), margin + 130, yPosition);
  doc.text(`$${totalCost.toFixed(2)}`, margin + 170, yPosition);

  // Save the PDF
  const fileName = `raw-materials-report-${donor?.donorId || 'unknown'}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

const FinalizeRawMaterialsButton = ({ donor, onFinalize }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rawMaterialUsage = donor?.rawMaterialUsage || [];
  
  // Calculate total cost
  const totalCost = rawMaterialUsage.reduce((sum, usage) => {
    const cost = (parseFloat(usage.costPerItem) || 0) * (usage.quantityUsed || 0);
    return sum + cost;
  }, 0);

  const totalQuantity = rawMaterialUsage.reduce((sum, usage) => sum + (usage.quantityUsed || 0), 0);

  // Group materials for invoice-style display
  const groupedMaterials = groupRawMaterialsByItem(rawMaterialUsage);

  const handleExportPDF = () => {
    try {
      generatePDFReport(donor, groupedMaterials, totalCost, totalQuantity);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF report');
    }
  };

  const handleFinalize = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/finalize-raw-materials`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to finalize raw materials');
      }

      setOpen(false);
      if (onFinalize) await onFinalize();
    } catch (err) {
      console.error('Error finalizing raw materials:', err);
      setError(err.message || 'Failed to finalize raw materials');
    } finally {
      setLoading(false);
    }
  };

  // Already finalized - show status with PDF export
  if (donor?.rawMaterialsFinalized) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
        <CheckCircleIcon color="success" fontSize="large" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight="bold" color="success.dark">
            Raw Materials Finalized
          </Typography>
          <Typography variant="body2" color="success.dark">
            Total Cost: <strong>${parseFloat(donor.totalRawMaterialCost || 0).toFixed(2)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Finalized by {donor.rawMaterialsFinalizedByName} on {formatDate(donor.rawMaterialsFinalizedAt)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PdfIcon />}
          onClick={handleExportPDF}
          sx={{ ml: 2 }}
        >
          Export PDF
        </Button>
      </Box>
    );
  }

  // No raw materials used yet
  if (rawMaterialUsage.length === 0) {
    return (
      <Alert severity="info">
        No raw materials have been added yet. Add raw materials before finalizing.
      </Alert>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          color="warning"
          size="large"
          startIcon={<LockIcon />}
          onClick={() => setOpen(true)}
          sx={{ 
            px: 4, 
            py: 1.5, 
            fontWeight: 'bold',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6
            }
          }}
        >
          Finalize Raw Materials (${totalCost.toFixed(2)})
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<PdfIcon />}
          onClick={handleExportPDF}
          sx={{ px: 3, py: 1.5 }}
        >
          Export PDF
        </Button>
      </Stack>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="warning" />
            <Typography variant="h6">Finalize Raw Materials</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Once finalized, raw materials cannot be modified. 
              Review the summary below before confirming.
            </Typography>
          </Alert>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Raw Materials Invoice Summary
          </Typography>

          {/* Invoice-style grouped materials table */}
          <Box sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Dates Used</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedMaterials.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.itemDescription}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lot: {item.lotNumber} | Vendor: {item.vendor}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.dates.map((date, dateIndex) => (
                          <Chip
                            key={dateIndex}
                            label={date}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.totalQuantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        ${item.costPerItem.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ${item.totalCost.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    TOTAL
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {totalQuantity}
                  </TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    ${totalCost.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Summary
          </Typography>

          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Donor ID</TableCell>
                <TableCell align="right">
                  <Chip label={donor?.donorId} size="small" color="primary" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Unique Items</TableCell>
                <TableCell align="right">{groupedMaterials.length}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Usage Entries</TableCell>
                <TableCell align="right">{rawMaterialUsage.length}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Quantity</TableCell>
                <TableCell align="right">{totalQuantity} units</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Total Cost</TableCell>
                <TableCell align="right">
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    ${totalCost.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            This cost will be recorded and locked for this donor. An admin will review 
            this before the donor can proceed to the next phase.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleExportPDF}
            disabled={loading}
          >
            Export PDF
          </Button>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleFinalize}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
          >
            {loading ? 'Finalizing...' : 'Confirm Finalization'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinalizeRawMaterialsButton;

